/**
 * Environment Configuration
 *
 * Centralised access to environment variables with type safety and validation.
 * All environment variables should be accessed through this module.
 */

interface EnvironmentConfig {
  // API
  apiUrl: string;
  apiTimeout: number;

  // Environment
  environment: 'development' | 'staging' | 'production';
  isDevelopment: boolean;
  isProduction: boolean;

  // Features
  enableAuditTrail: boolean;
  enableDataSharingIndicators: boolean;

  // Session
  sessionTimeout: number;
  sessionWarningBefore: number;

  // Error Tracking
  sentryDsn: string | null;

  // Analytics
  gaTrackingId: string | null;

  // Cognito (optional — only used when VITE_AUTH_MODE=cognito)
  cognitoUserPoolId: string;
  cognitoClientId: string;
  authMode: string;
}

// Vite replaces `import.meta.env.VITE_X` STATICALLY, at build time, by
// matching that exact text. A dynamic lookup — `import.meta.env[key]` —
// is not replaced and reads undefined in a production bundle, while working
// fine in dev. That failure mode is silent and ships: every variable would
// fall back to its default, which for apiUrl means every API call in the
// deployed app going to the user's own localhost.
//
// So each variable is referenced once, statically, here. The helpers below
// read this map rather than the environment, and adding a variable means
// adding a line to it.
const ENV: Record<string, string | undefined> = {
  VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
  VITE_API_URL: import.meta.env.VITE_API_URL,
  VITE_API_TIMEOUT: import.meta.env.VITE_API_TIMEOUT,
  VITE_ENABLE_AUDIT_TRAIL: import.meta.env.VITE_ENABLE_AUDIT_TRAIL,
  VITE_ENABLE_DATA_SHARING_INDICATORS:
    import.meta.env.VITE_ENABLE_DATA_SHARING_INDICATORS,
  VITE_SESSION_TIMEOUT: import.meta.env.VITE_SESSION_TIMEOUT,
  VITE_SESSION_WARNING_BEFORE: import.meta.env.VITE_SESSION_WARNING_BEFORE,
  VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
  VITE_GA_TRACKING_ID: import.meta.env.VITE_GA_TRACKING_ID,
  VITE_COGNITO_USER_POOL_ID: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  VITE_COGNITO_CLIENT_ID: import.meta.env.VITE_COGNITO_CLIENT_ID,
  VITE_AUTH_MODE: import.meta.env.VITE_AUTH_MODE,
};

function getEnvVar(key: string, defaultValue?: string): string {
  const value = ENV[key];
  if (value === undefined || value === '') {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    console.warn(`Environment variable ${key} is not set`);
    return '';
  }
  return value;
}

function getEnvVarAsNumber(key: string, defaultValue: number): number {
  const value = ENV[key];
  if (value === undefined || value === '') {
    return defaultValue;
  }
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
}

function getEnvVarAsBoolean(key: string, defaultValue: boolean): boolean {
  const value = ENV[key];
  if (value === undefined || value === '') {
    return defaultValue;
  }
  return value.toLowerCase() === 'true';
}

const environment = getEnvVar('VITE_ENVIRONMENT', 'development') as EnvironmentConfig['environment'];

export const config: EnvironmentConfig = {
  // API
  apiUrl: getEnvVar('VITE_API_URL', 'http://localhost:8000'),
  // 2 minutes by default. Most API calls return in well under a
  // second, but submit-form / claim-sheet derive can run propagation
  // logic across all engagement snapshots and routinely take 30-60s
  // on large supplier accounts. The previous 30s default surfaced as
  // "Request timeout" mid-save for those users.
  apiTimeout: getEnvVarAsNumber('VITE_API_TIMEOUT', 120000),

  // Environment
  environment,
  isDevelopment: environment === 'development',
  isProduction: environment === 'production',

  // Features
  enableAuditTrail: getEnvVarAsBoolean('VITE_ENABLE_AUDIT_TRAIL', true),
  enableDataSharingIndicators: getEnvVarAsBoolean('VITE_ENABLE_DATA_SHARING_INDICATORS', true),

  // Session — generous inactivity timeout for data-entry-heavy workflows
  sessionTimeout: getEnvVarAsNumber('VITE_SESSION_TIMEOUT', 86400000), // 24 hours
  sessionWarningBefore: getEnvVarAsNumber('VITE_SESSION_WARNING_BEFORE', 600000), // 10 minutes

  // Error Tracking
  sentryDsn: getEnvVar('VITE_SENTRY_DSN', '') || null,

  // Analytics
  gaTrackingId: getEnvVar('VITE_GA_TRACKING_ID', '') || null,

  // Cognito
  cognitoUserPoolId: getEnvVar('VITE_COGNITO_USER_POOL_ID', ''),
  cognitoClientId: getEnvVar('VITE_COGNITO_CLIENT_ID', ''),
  authMode: getEnvVar('VITE_AUTH_MODE', 'local'),
};

// Validate critical configuration in production
if (config.isProduction) {
  if (!config.apiUrl.startsWith('https://')) {
    console.error('SECURITY WARNING: API URL should use HTTPS in production');
  }
}

export default config;
